import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';
import Thumbnail from 'components/atomic/atoms/thumbnail';

export interface FannedThumbnailImage {
  src?: string | null;
  alt?: string;
}

export interface FannedThumbnailsProps {
  /** Up to 3 images, placed in fan order: 1st → left, 2nd → right, 3rd → center (on top). */
  images: FannedThumbnailImage[];
  /** When true the fan spreads open. Controlled by the parent so a larger surrounding control can be the trigger. */
  hovered?: boolean;
  /** Edge length of the (square) positioning area. Defaults to '4rem'. */
  size?: number | string;
  /** Edge length of each thumbnail. Defaults to '2.5rem'. */
  thumbnailSize?: number | string;
  /** Fallback image for empty/broken sources. */
  fallbackSrc?: string;
  /** MUI sx overrides for the container (merged last). */
  sx?: SxProps<Theme>;
}

/** Fixed 3-slot fan geometry: rest vs. spread-open transforms, keyed by index. */
const FAN_SLOTS = [
  {
    key: 'left',
    zIndex: 10,
    rest: 'translate(-50%, -50%) rotate(-9deg) translate(-7px, 1px)',
    open: 'translate(-50%, -50%) rotate(-18deg) translate(-14px, -1px)',
  },
  {
    key: 'right',
    zIndex: 10,
    rest: 'translate(-50%, -50%) rotate(9deg) translate(7px, 1px)',
    open: 'translate(-50%, -50%) rotate(18deg) translate(14px, -1px)',
  },
  {
    key: 'center',
    zIndex: 20,
    rest: 'translate(-50%, -50%)',
    open: 'translate(-50%, -50%) translate(0, -3px)',
  },
] as const;

/**
 * FannedThumbnails
 *
 * Atomic level: Molecule.
 *
 * A small stack of overlapping thumbnails that fan out when `hovered`. Reuses the
 * `Thumbnail` atom for each image (cover crop, rounded corners, graceful
 * fallback). Hover state is controlled by the parent so the whole surrounding
 * control can act as the trigger. Zero domain knowledge.
 */
export function FannedThumbnails({
  images,
  hovered = false,
  size = '4rem',
  thumbnailSize = '2.5rem',
  fallbackSrc,
  sx,
}: FannedThumbnailsProps) {
  return (
    <Box
      sx={[
        { position: 'relative', width: size, height: size, flexShrink: 0 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {images.slice(0, FAN_SLOTS.length).map((image, index) => {
        const slot = FAN_SLOTS[index];
        return (
          <Thumbnail
            key={slot.key}
            src={image.src}
            alt={image.alt ?? ''}
            size={thumbnailSize}
            radius="10px"
            fallbackSrc={fallbackSrc}
            sizes="40px"
            unoptimized
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              border: '1px solid #fff',
              backgroundColor: '#fff',
              boxShadow: '0 1px 4px rgba(15,23,41,0.12)',
              transition: 'transform 400ms ease-out',
              transform: hovered ? slot.open : slot.rest,
              zIndex: slot.zIndex,
            }}
          />
        );
      })}
    </Box>
  );
}

export default FannedThumbnails;
